import { h, Fragment } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import ChevronDown from '../utils/icon/chevron-down.svg';
import CountrySelectorModal from './country-selector-modal';
import extractURLParams from '../utils/utm';

const modalsContainer = document.getElementById('modals-container');

const CountrySelector = ({
  countries,
  languages,
  locale,
  message,
  hreflangUrl,
  countryISO,
  hideCloseBtnSetting,
  autoRedirect: defaultAutoRedirect,
  canadianStoreUrl,
  internationalStoreUrl,
  hidePopup,
}) => {
  const autoRedirect = defaultAutoRedirect !== false;

  const country = useMemo(
    () => countries.find((i) => i.iso_code.find((code) => code.toLowerCase().trim() === countryISO?.toLowerCase())),
    [countries, countryISO],
  );
  const language = useMemo(
    () => languages.find((i) => i.iso_code.toLowerCase() === locale.toLowerCase()),
    [languages, locale],
  );

  const [isModalActive, setIsModalActive] = useState(false);
  const [userLocation, setUserLocation] = useState({});

  const container = useRef(null);
  const showModal = () => {
    document.body.classList.add('no-scroll');
    setIsModalActive(true);
  };
  const hideModal = () => {
    document.body.classList.remove('no-scroll');
    setIsModalActive(false);
  };

  const getCountry = async () => {
    const params = new URLSearchParams(window.location.search);
    const shopifyLocationResponse = await fetch(
      // eslint-disable-next-line max-len
      `${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true`,
    ).then((r) => r.json());

    const geoData = {
      country: shopifyLocationResponse?.detected_values?.country?.handle,
      language: shopifyLocationResponse?.suggestions?.[0]?.parts?.language?.handle,
    };

    setUserLocation(geoData);
    let expire = new Date(Date.now() + 10 * 864e5).toUTCString();
    // eslint-disable-next-line max-len
    document.cookie = `geo_data=${JSON.stringify(geoData)}; expires=${expire}; path=/`;

    if (params.has('geo')) {
      expire = new Date(Date.now() + 300 * 864e5).toUTCString();
      document.cookie = `geo_selected=1; expires=${expire}; path=/`;
    } else if (!autoRedirect) {
      if (hidePopup === 'false') showModal();
    }
  };

  useEffect(() => {
    if (Object.keys(userLocation).length === 0) return;
    if (/dev/.test(window.location.href)) return;

    const params = new URLSearchParams(window.location.search);
    const isCountrySelected = document.cookie.match('(^|; )force_geo=([^;]*)') || 0;

    const storeCountry = Shopify.country;
    const userCountry = userLocation.country;
    const geoParam = {
      geo: 1,
    };

    const utmParam = params.toString().includes('utm') ? extractURLParams(window.location.href) : {};

    const finalParams = {
      ...utmParam,
      ...geoParam,
    };

    const geoString = `?${new URLSearchParams(finalParams).toString()}`;

    if (!autoRedirect || params.has('geo') || Shopify.designMode) return;

    if (storeCountry !== userCountry && !isCountrySelected) {
      // set cookie to prevent redirecting twice.
      const expire = new Date(Date.now() + 10 * 864e5).toUTCString();
      document.cookie = `force_geo=1; expires=${expire}; path=/`;

      // redirect to the country detected
      if (userCountry === 'CA') {
        // redirect to the canadian store
        const canadaStore = canadianStoreUrl || 'https://saje.ca';
        window.location.href = canadaStore + geoString;
      } else {
        // redirect to the US store
        const internationalStore = internationalStoreUrl || 'https://saje.com';
        window.location.href = internationalStore + geoString;
      }
    }
  }, [userLocation]);

  useEffect(() => {
    document.querySelectorAll('[data-country-selector]').forEach((element) => {
      element.addEventListener('click', showModal);
    });
    const geoData = (document.cookie.match('(^|; )geo_data=([^;]*)') || 0)[2];
    if (!geoData) {
      getCountry();
    } else {
      setUserLocation(JSON.parse(geoData));
    }
    return () => {
      document.querySelectorAll('[data-country-selector]').forEach((element) => {
        element.removeEventListener('click', showModal);
      });
    };
  }, []);

  useEffect(() => {
    if (container.current) {
      document.querySelectorAll('[data-country-selector]').forEach((element) => {
        // eslint-disable-next-line no-param-reassign
        element.innerHTML = container.current.outerHTML;
      });
    }
  }, [country]);

  if (!country) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-2 cursor-pointer" ref={container}>
        <img height="17" width="23" src={country.flag_icon} alt={country.iso_code} className="w-auto h-[17px]" />
        <span className="whitespace-nowrap overflow-hidden text-ellipsis text-body-2">
          {country.name} | {locale.toUpperCase()}
        </span>
        <ChevronDown />
      </div>

      {isModalActive &&
        createPortal(
          <CountrySelectorModal
            countries={countries}
            languages={languages}
            message={message}
            hreflangUrl={hreflangUrl}
            defaultCountry={country}
            defaultLanguage={language}
            geoLocation={userLocation}
            onClose={hideModal}
            hideCloseBtnSetting={hideCloseBtnSetting}
            autoRedirect={autoRedirect}
          />,
          modalsContainer,
        )}
    </>
  );
};

export default CountrySelector;
